/**
 * ReportsPage Component
 * Main page for Reports & Analytics with tabs for different report types
 *
 * Story 1.1: Reports Navigation & Page Structure
 * Story 1.2: Reports Permission Model
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUrlState } from '../../hooks/useUrlState';
import { decodeUrlList, encodeUrlList } from '../../utils/urlList';
import { isUuid, resolveIdSelection } from '../../utils/departmentSelection';
import { useSearchParams } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Activity,
  UserPlus,
  Download,
  Lock,
  Filter
} from 'lucide-react';
import { Button, Card, ExportModal } from '../../components/common';
import {
  DateRangeSelector,
  useDateRangeParams,
  ReportFilterPanel,
  DEFAULT_REPORT_FILTERS,
  hasActiveReportFilters,
  ExecutiveDashboard,
  ExecutiveDashboardSkeleton,
  EngagementReportSkeleton,
  OKRHealthReportSkeleton,
  UserAdoptionReportSkeleton,
  EngagementDashboard,
  OKRHealthDashboard,
  UserAdoptionDashboard,
  ReportScopeIndicator,
  type ReportFilters,
} from '../../components/reports';
import { useReportPermissions } from '../../hooks';
import { useReportFilterOptions, useReportScope } from '../../hooks/useReportData';

// Tab type definitions
type ReportTabType = 'executive' | 'engagement' | 'health' | 'adoption';

/**
 * Tabs the filter panel actually drives (D-B3).
 *
 * It used to render on every tab while `reportFilters` reached only the
 * Executive dashboard — and after C1 a manager cannot open Executive at all, so
 * a manager was offered a filter panel that provably did nothing. It was then
 * limited to Executive and Health. Since 2026-09-29 Engagement and Adoption
 * consume the filters too (every card narrows to the matching people), so the
 * panel drives all four tabs. Keep this set equal to the tabs that pass
 * `filters={reportFilters}` below.
 */
const TABS_WITH_FILTERS: ReadonlySet<ReportTabType> = new Set(['executive', 'engagement', 'health', 'adoption']);

interface TabConfig {
  id: ReportTabType;
  label: string;
  icon: React.ElementType;
  description: string;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
}

// Tab configuration
const reportTabs: TabConfig[] = [
  {
    id: 'executive',
    label: 'Executive Dashboard',
    icon: LayoutDashboard,
    description: 'High-level insights into OKR performance',
  },
  {
    id: 'engagement',
    label: 'Engagement',
    icon: Users,
    description: 'User activity and feature adoption metrics',
  },
  {
    id: 'health',
    label: 'OKR Health',
    icon: Activity,
    description: 'Progress tracking and risk indicators',
  },
  {
    id: 'adoption',
    label: 'User Adoption',
    icon: UserPlus,
    description: 'Onboarding and retention metrics',
    adminOnly: true,
  },
];

export const ReportsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Use the dedicated report permissions hook
  const {
    canViewReports,
    canExportReports,
    canViewExecutiveDashboard,
    canViewEngagementReport,
    canViewHealthReport,
    canViewAdoptionReport,
    isLoading,
  } = useReportPermissions();

  // Date range state (persists across tabs)
  const { dateRange, setDateRange } = useDateRangeParams('this_quarter');

  // Whose numbers these are (Item B). Absent for an unrestricted caller, so an
  // account_admin's header is unchanged. Not fetched until we know the caller
  // may read reports at all.
  const { scope } = useReportScope(canViewReports);

  // Filter state (persists across tabs). In the URL, so a drill-down into the OKR list and
  // Back keeps it; in component state it reset to "everyone".
  const [filterView, setFilterView] = useUrlState({ rdept: '', rteam: '', rowner: '' });

  // Departments and teams are uuids. Links from before the Department/Team model carry NAMES
  // (`?rdept=Sales`), which the API answers with 422 — and dropping them would show
  // organisation-wide numbers under a "Sales" chip. So a name is resolved against the filter
  // options first; until they arrive the filtered tabs show their skeleton and send nothing.
  // If the options cannot load, names are dropped and ids kept rather than waiting forever.
  const filterOptions = useReportFilterOptions(canViewReports);
  const departmentSelection = useMemo(() => {
    const raw = decodeUrlList(filterView.rdept);
    return filterOptions.isError
      ? resolveIdSelection(raw.filter(isUuid), undefined)
      : resolveIdSelection(raw, filterOptions.data?.departments);
  }, [filterView.rdept, filterOptions.data, filterOptions.isError]);
  const teamSelection = useMemo(() => {
    const raw = decodeUrlList(filterView.rteam);
    return filterOptions.isError
      ? resolveIdSelection(raw.filter(isUuid), undefined)
      : resolveIdSelection(raw, filterOptions.data?.teams);
  }, [filterView.rteam, filterOptions.data, filterOptions.isError]);
  const filtersPending = departmentSelection.pending || teamSelection.pending;

  const reportFilters: ReportFilters = useMemo(
    () => ({
      ...DEFAULT_REPORT_FILTERS,
      departmentIds: departmentSelection.ids,
      teamIds: teamSelection.ids,
      ownerIds: decodeUrlList(filterView.rowner),
    }),
    [departmentSelection.ids, teamSelection.ids, filterView.rowner]
  );
  const setReportFilters = useCallback(
    (next: ReportFilters) =>
      setFilterView({
        rdept: encodeUrlList(next.departmentIds),
        rteam: encodeUrlList(next.teamIds),
        rowner: encodeUrlList(next.ownerIds),
      }),
    [setFilterView]
  );

  // Once resolved against the options, put the ids in the URL so the name is not resolved
  // again and a shared link is id-only. `useUrlState` writes with `replace`, so Back does not
  // return to the name URL.
  useEffect(() => {
    if (!filterOptions.data) return;
    const rdept = encodeUrlList(departmentSelection.ids);
    const rteam = encodeUrlList(teamSelection.ids);
    if (rdept !== filterView.rdept || rteam !== filterView.rteam) {
      setFilterView({ rdept, rteam });
    }
  }, [
    filterOptions.data,
    departmentSelection.ids,
    teamSelection.ids,
    filterView.rdept,
    filterView.rteam,
    setFilterView,
  ]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // The URL is the ONLY source of the active tab. It used to be copied into useState once, on
  // mount, so Back/Forward changed `?tab` while the page kept showing the previous tab.
  const tabFromUrl = searchParams.get('tab') as ReportTabType | null;
  const activeTab: ReportTabType = tabFromUrl || 'executive';

  // Handle tab change with URL state
  const handleTabChange = (tab: ReportTabType) => {
    // Only `tab` is touched: the date range and filters "persist across tabs" in the URL too.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'executive') next.delete('tab');
        else next.set('tab', tab);
        return next;
      }
    );
    // Leaving an open panel behind on a tab it does not drive would be the
    // same inert control by another route.
    if (!TABS_WITH_FILTERS.has(tab)) {
      setIsFilterPanelOpen(false);
    }
  };

  // Export modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Filter tabs based on permissions
  const visibleTabs = reportTabs.filter(tab => {
    switch (tab.id) {
      case 'executive':
        return canViewExecutiveDashboard;
      case 'engagement':
        return canViewEngagementReport;
      case 'health':
        return canViewHealthReport;
      case 'adoption':
        return canViewAdoptionReport;
      default:
        return canViewReports;
    }
  });

  // The tab LIST has always been permission-filtered, but the selected tab was
  // not: it defaulted to 'executive' unconditionally, so a manager opening
  // /reports landed on an admin-only tab and saw nothing but its 403. Fall back
  // to the first tab the caller can actually view. Undefined only while
  // permissions are still loading, which the content area guards on.
  const effectiveTab: ReportTabType | undefined = visibleTabs.some(
    (tab) => tab.id === activeTab
  )
    ? activeTab
    : visibleTabs[0]?.id;

  // Only Executive and Health consume the filters; the button is hidden
  // elsewhere rather than left inert (D-B3).
  const showFilterButton = effectiveTab !== undefined && TABS_WITH_FILTERS.has(effectiveTab);

  // Show access denied if user doesn't have any report permissions
  if (!isLoading && !canViewReports) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <Lock className="h-12 w-12 text-stone-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-stone-900 mb-2">Access Restricted</h2>
          <p className="text-stone-500">
            You don't have permission to view reports. Please contact your administrator
            if you believe this is an error.
          </p>
        </Card>
      </div>
    );
  }

  // Render tab content
  const renderTabContent = () => {
    // A filtered tab waits for a legacy name to resolve: never a request with a name, never
    // organisation-wide numbers under a chip.
    if (filtersPending && effectiveTab !== undefined && TABS_WITH_FILTERS.has(effectiveTab)) {
      return (
        <div data-testid="report-filters-resolving">
          {effectiveTab === 'executive' ? (
            <ExecutiveDashboardSkeleton />
          ) : effectiveTab === 'engagement' ? (
            <EngagementReportSkeleton />
          ) : effectiveTab === 'adoption' ? (
            <UserAdoptionReportSkeleton />
          ) : (
            <OKRHealthReportSkeleton />
          )}
        </div>
      );
    }

    switch (effectiveTab) {
      case 'executive':
        return (
          <ExecutiveDashboard
            dateRange={dateRange}
            filters={reportFilters}
          />
        );
      case 'engagement':
        return (
          <EngagementDashboard
            dateRange={dateRange}
            filters={reportFilters}
          />
        );
      case 'health':
        return (
          <OKRHealthDashboard
            dateRange={dateRange}
            filters={reportFilters}
          />
        );
      case 'adoption':
        return (
          <UserAdoptionDashboard
            dateRange={dateRange}
            filters={reportFilters}
          />
        );
      default:
        // Fail closed. This branch is reached only before permissions resolve
        // (no visible tabs yet); rendering the Executive dashboard here was the
        // second route onto an admin-only tab for a caller who cannot view it.
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <div className="bg-gradient-to-r from-stone-800 via-stone-700 to-primary-600">
        <div className="px-4 sm:px-6 lg:px-10 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-2xl sm:text-3xl font-semibold text-white"
                style={{ fontFamily: 'Fraunces, Georgia, serif' }}
              >
                Reports & Analytics
              </h1>
              <p className="mt-1 text-sm text-stone-300">
                Insights and metrics for your OKR program
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <ReportScopeIndicator scope={scope} />
              <DateRangeSelector
                value={dateRange}
                onChange={setDateRange}
                defaultPreset="this_quarter"
              />
              {showFilterButton && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsFilterPanelOpen(true)}
                  className={`bg-white/10 border-white/20 text-white hover:bg-white/20 ${
                    hasActiveReportFilters(reportFilters) ? 'ring-2 ring-primary-400' : ''
                  }`}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                  {hasActiveReportFilters(reportFilters) && (
                    <span className="ml-1 px-1.5 py-0.5 bg-primary-500 text-white text-xs font-bold rounded-full">
                      Active
                    </span>
                  )}
                </Button>
              )}
              {canExportReports && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsExportModalOpen(true)}
                  className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-10 py-6">
        {/* Tab Navigation */}
        <div className="border-b border-stone-200 mb-6">
          <nav className="flex gap-6" aria-label="Report Tabs">
            {visibleTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = effectiveTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`
                    flex items-center gap-2 py-3 px-1 border-b-2 transition-colors font-medium text-sm
                    ${isActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-stone-500 hover:text-stone-700 hover:border-stone-300'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                  title={tab.description}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Filter Panel — Executive and Health only */}
      {showFilterButton && (
        <ReportFilterPanel
          isOpen={isFilterPanelOpen}
          onClose={() => setIsFilterPanelOpen(false)}
          filters={reportFilters}
          onFiltersChange={setReportFilters}
        />
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        availableTypes={['objectives', 'key_results', 'both']}
        title="Export Report Data"
      />
    </div>
  );
};

